/**
 * CO₂ Emission Factors for Electric Vehicles by Province/State
 * Source: Environment Canada & U.S. EIA
 * Units: grams of CO₂ per kWh
 */

export const CANADA_EMISSION_FACTORS: Record<string, number> = {
  'Alberta': 600,
  'British Columbia': 12,
  'Manitoba': 2,
  'New Brunswick': 340,
  'Newfoundland and Labrador': 40,
  'Northwest Territories': 190,
  'Nova Scotia': 700,
  'Nunavut': 750,
  'Ontario': 30,
  'Prince Edward Island': 20,
  'Quebec': 1.5,
  'Saskatchewan': 650,
  'Yukon': 80
};

export const US_EMISSION_FACTORS: Record<string, number> = {
  'Alabama': 450,
  'Alaska': 520,
  'Arizona': 450,
  'Arkansas': 510,
  'California': 240,
  'Colorado': 560,
  'Connecticut': 280,
  'Delaware': 470,
  'Florida': 450,
  'Georgia': 450,
  'Hawaii': 650,
  'Idaho': 80,
  'Illinois': 420,
  'Indiana': 730,
  'Iowa': 560,
  'Kansas': 610,
  'Kentucky': 770,
  'Louisiana': 500,
  'Maine': 280,
  'Maryland': 490,
  'Massachusetts': 380,
  'Michigan': 520,
  'Minnesota': 470,
  'Mississippi': 450,
  'Missouri': 700,
  'Montana': 580,
  'Nebraska': 560,
  'Nevada': 440,
  'New Hampshire': 260,
  'New Jersey': 310,
  'New Mexico': 540,
  'New York': 260,
  'North Carolina': 420,
  'North Dakota': 680,
  'Ohio': 620,
  'Oklahoma': 560,
  'Oregon': 200,
  'Pennsylvania': 490,
  'Rhode Island': 450,
  'South Carolina': 340,
  'South Dakota': 430,
  'Tennessee': 430,
  'Texas': 450,
  'Utah': 650,
  'Vermont': 30,
  'Virginia': 410,
  'Washington': 130,
  'West Virginia': 810,
  'Wisconsin': 540,
  'Wyoming': 730
};

/**
 * Fuel Emission Factors
 * Gasoline: 2.3 kg CO₂ per liter
 * Diesel: 2.7 kg CO₂ per liter
 */
export const FUEL_EMISSION_FACTORS = {
  gasoline: 2.3, // kg CO₂ per liter
  diesel: 2.7    // kg CO₂ per liter
};

/**
 * Default values for CO₂ calculations
 */
export const CO2_DEFAULTS = {
  manufacturingEmissionsNew: 10000, // kg CO₂ for new car manufacturing
  manufacturingEmissionsUsed: 0,    // kg CO₂ for used car (already manufactured)
  socialCostOfCarbonCAD: 65,        // CAD per tonne
  socialCostOfCarbonUSD: 51,        // USD per tonne
  defaultElectricityCostCAD: 0.13,  // CAD per kWh
  defaultElectricityCostUSD: 0.12,  // USD per kWh
  pluginHybridElectricPercent: {
    high: 0.85,   // 85% electric, 15% gas
    medium: 0.55, // 55% electric, 45% gas
    low: 0.25     // 25% electric, 75% gas
  }
};

/**
 * Get emission factor for a province/state
 */
export function getEmissionFactor(region: 'Canada' | 'US', provinceOrState: string): number {
  const factors = region === 'Canada' ? CANADA_EMISSION_FACTORS : US_EMISSION_FACTORS;
  return factors[provinceOrState] || 400; // Default if not found
}

/**
 * Get default Social Cost of Carbon for region
 */
export function getDefaultSCC(region: 'Canada' | 'US'): number {
  return region === 'Canada' ? CO2_DEFAULTS.socialCostOfCarbonCAD : CO2_DEFAULTS.socialCostOfCarbonUSD;
}

/**
 * Get default electricity cost for region
 */
export function getDefaultElectricityCost(region: 'Canada' | 'US'): number {
  return region === 'Canada' ? CO2_DEFAULTS.defaultElectricityCostCAD : CO2_DEFAULTS.defaultElectricityCostUSD;
}
